import { useEffect, useRef, useState } from 'react'
import { resolveTargetRect, waitForTarget } from '../../utils/tutorialTargets'
import TutorialCallout from './TutorialCallout'
import TutorialStepRail from './TutorialStepRail'
import './tutorial.css'

/**
 * SpotlightLayer — positioned ring + connector + callout.
 *
 * Targeted rect from data-tutorial hooks with a ~2s retry for lazy views;
 * if the target never mounts, falls back to a centered panel (art spec §2/§6)
 * — never a dangling arrow.
 */

const PAD = 8 // ring padding around target rect (§4)

function computePlacement(rect, calloutSize) {
    const vw = window.innerWidth
    const vh = window.innerHeight
    const gap = 24 // connector length
    const cw = calloutSize.width || 340
    const ch = calloutSize.height || 180
    // Prefer below, then right, then above, then left.
    if (rect.y + rect.height + gap + ch < vh - 12) return 'below'
    if (rect.x + rect.width + gap + cw < vw - 12) return 'right'
    if (rect.y - gap - ch > 12) return 'above'
    if (rect.x - gap - cw > 12) return 'left'
    return 'center'
}

export default function SpotlightLayer({ step, beatIndex, onNextBeat, onAck, onWait, onNavigate, onCollapse, onSkip }) {
    const beat = step.beats[Math.min(beatIndex, step.beats.length - 1)]
    const [rect, setRect] = useState(null)
    const [targetMissing, setTargetMissing] = useState(false)
    const [calloutSize, setCalloutSize] = useState({ width: 340, height: 180 })
    const litElRef = useRef(null)

    // Esc collapses (never skips — §6/§7).
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onSkip.collapseEsc()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [onSkip])
    useEffect(() => {
        let cancelled = false
        const target = beat.target
        if (!target) {
            setTargetMissing(true)
            setRect(null)
            return undefined
        }
        setTargetMissing(false)
        setRect(null)
        waitForTarget(target).then((res) => {
            if (cancelled) return
            if (res.ok) setRect(res.rect)
            else setTargetMissing(true)
        })
        const onReflow = () => {
            const r = resolveTargetRect(target)
            if (r.ok) setRect(r.rect)
        }
        window.addEventListener('resize', onReflow)
        window.addEventListener('scroll', onReflow, true)
        const iv = setInterval(onReflow, 500)
        return () => {
            cancelled = true
            window.removeEventListener('resize', onReflow)
            window.removeEventListener('scroll', onReflow, true)
            clearInterval(iv)
        }
    }, [beat, step.id])

    // Brighten the spotlight target: the game theme is near-black, so the
    // undimmed ring hole alone reads as "another dark section" (contrast bug).
    // Runs after rect resolves (target mounted); no-ops when unchanged so the
    // 500ms reflow poll doesn't churn the class.
    useEffect(() => {
        if (!rect || !beat.target) return undefined
        const el = document.querySelector(`[data-tutorial="${beat.target}"]`)
        if (!el) return undefined
        if (litElRef.current && litElRef.current !== el) litElRef.current.classList.remove('tcd-target-lit')
        el.classList.add('tcd-target-lit')
        litElRef.current = el
        return undefined
    }, [rect, beat, step.id])

    // Unmount: always un-light, even if the last rect never resolved.
    useEffect(() => () => {
        if (litElRef.current) litElRef.current.classList.remove('tcd-target-lit')
    }, [])

    // Measure callout for placement math.
    useEffect(() => {
        const el = document.querySelector('.tcd-callout')
        if (el) setCalloutSize({ width: el.offsetWidth, height: el.offsetHeight })
    })

    if (targetMissing) {
        // Centered panel fallback; the callout renders at viewport center.
        return (
            <>
                <div className="tcd-scrim tcd-catcher" onClick={onCollapse} aria-hidden="true" />
                <TutorialStepRail currentStep={step.id} />
                <TutorialCallout
                    step={step}
                    beat={beat}
                    isLastBeat
                    centered
                    onNextBeat={onNextBeat}
                    onAck={onAck}
                    onWait={onWait}
                    onNavigate={onNavigate}
                    onSkip={onSkip}
                />
            </>
        )
    }

    if (!rect) return null // resolving — one quiet frame, no flash

    const ringLeft = rect.x - PAD
    const ringTop = rect.y - PAD
    const ringWidth = rect.width + PAD * 2
    const ringHeight = rect.height + PAD * 2
    const placement = computePlacement(rect, calloutSize)

    // Callout anchor per placement (free axis set inline).
    const calloutStyle = {}
    if (placement === 'below') {
        calloutStyle.left = Math.min(Math.max(12, rect.x + rect.width / 2 - calloutSize.width / 2), window.innerWidth - calloutSize.width - 12)
        calloutStyle.top = ringTop + ringHeight
    } else if (placement === 'above') {
        calloutStyle.left = Math.min(Math.max(12, rect.x + rect.width / 2 - calloutSize.width / 2), window.innerWidth - calloutSize.width - 12)
        calloutStyle.top = ringTop - 24
        calloutStyle.transform = 'translateY(-100%)'
    } else if (placement === 'right') {
        calloutStyle.left = ringLeft + ringWidth
        calloutStyle.top = Math.min(Math.max(12, rect.y - 8), window.innerHeight - calloutSize.height - 12)
    } else if (placement === 'left') {
        calloutStyle.left = ringLeft
        calloutStyle.top = Math.min(Math.max(12, rect.y - 8), window.innerHeight - calloutSize.height - 12)
        calloutStyle.transform = 'translateX(calc(-100% - 24px))'
    } else {
        calloutStyle.left = '50%'
        calloutStyle.top = '50%'
        calloutStyle.transform = 'translate(-50%, -50%)'
    }

    // Connector: 24px stub between ring edge and callout edge.
    const connectorStyle = {}
    if (placement === 'below') {
        connectorStyle.left = rect.x + rect.width / 2 - 1
        connectorStyle.top = ringTop + ringHeight
        connectorStyle.width = 2
        connectorStyle.height = 24
    } else if (placement === 'above') {
        connectorStyle.left = rect.x + rect.width / 2 - 1
        connectorStyle.top = ringTop - 24
        connectorStyle.width = 2
        connectorStyle.height = 24
    } else if (placement === 'right') {
        connectorStyle.left = ringLeft + ringWidth
        connectorStyle.top = rect.y + rect.height / 2 - 1
        connectorStyle.width = 24
        connectorStyle.height = 2
    } else if (placement === 'left') {
        connectorStyle.left = ringLeft - 24
        connectorStyle.top = rect.y + rect.height / 2 - 1
        connectorStyle.width = 24
        connectorStyle.height = 2
    }

    const calloutEl = (
        <TutorialCallout
            step={step}
            beat={beat}
            isLastBeat={Math.min(beatIndex, step.beats.length - 1) === step.beats.length - 1}
            style={calloutStyle}
            placement={placement}
            onNextBeat={onNextBeat}
            onAck={onAck}
            onWait={onWait}
            onNavigate={onNavigate}
            onSkip={onSkip}
        />
    )

    return (
        <>
            {/* Click-out catcher: any click outside the callout/rail tucks the
                log into the floating button (same contract as the welcome
                modal; Esc too). Beat position survives resume. */}
            <div className="tcd-catcher" onClick={onCollapse} aria-hidden="true" />
            <div
                className="tcd-spotlight-ring tcd-spotlight-ring--pulse"
                style={{ left: ringLeft, top: ringTop, width: ringWidth, height: ringHeight }}
                aria-hidden="true"
            />
            {placement !== 'center' && <div className="tcd-connector" style={connectorStyle} aria-hidden="true" />}
            <TutorialStepRail currentStep={step.id} />
            {calloutEl}
        </>
    )
}
